import React, { useState, useRef, useEffect } from 'react';
import { QAResult, QuestionSuggestion } from '../utils/dataQAEngine';
import { ColumnInfo } from '../utils/dataAnalyzer';
import { apiService } from '../domain/platform/apiService';
import './DataQA.css';

interface DataQAProps {
  datasetId: string;
  data: any[];
  headers: string[];
  columnInfo: Map<string, ColumnInfo>;
  fileName: string;
}

interface QAResponseView {
  question: string;
  answer: string;
  confidence: QAResult['confidence'];
  processingMethod: 'rule-based' | 'llm' | 'hybrid';
  citations?: Array<{ label: string; detail: string }>;
  followUpQuestions?: string[];
  relatedInsights?: string[];
  canAnswer: boolean;
}

const buildSuggestions = (headers: string[]): QuestionSuggestion[] => {
  const first = headers[0] || 'metric';
  const second = headers[1] || first;
  return [
    {
      category: 'Quick checks',
      icon: '📊',
      questions: [
        `How many records are in this dataset?`,
        `What is the average of ${first}?`,
        `What are the top ${second} values?`
      ]
    },
    {
      category: 'Reliability',
      icon: '🧪',
      questions: [
        'Any unusual outliers I should inspect?',
        `What trend do you see over time in ${first}?`
      ]
    }
  ];
};

export const DataQA: React.FC<DataQAProps> = ({ datasetId, headers, fileName }) => {
  const [question, setQuestion] = useState('');
  const [qaHistory, setQaHistory] = useState<QAResponseView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions] = useState<QuestionSuggestion[]>(buildSuggestions(headers));
  const [showSuggestions, setShowSuggestions] = useState(true);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [qaHistory]);

  const handleQuestionSubmit = async (questionText: string) => {
    if (!questionText.trim()) return;

    setIsLoading(true);
    setShowSuggestions(false);

    try {
      const result = await apiService.askQuestion(datasetId, questionText);
      setQaHistory((prev) => [
        ...prev,
        {
          question: questionText,
          answer: result.answer,
          confidence: result.confidence,
          canAnswer: true,
          processingMethod: 'rule-based',
          citations: result.citations
        }
      ]);
      setQuestion('');
    } catch (error) {
      console.error('Error processing question:', error);
      setQaHistory((prev) => [
        ...prev,
        {
          question: questionText,
          answer: 'The QA service failed for this request. Please try again.',
          confidence: 'low',
          canAnswer: false,
          processingMethod: 'rule-based',
          citations: [{ label: 'Service error', detail: 'No citation available for failed request.' }]
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleQuestionSubmit(question);
  };

  const handleSuggestionClick = (suggestionText: string) => {
    setQuestion(suggestionText);
    handleQuestionSubmit(suggestionText);
  };

  const clearHistory = () => {
    setQaHistory([]);
    setShowSuggestions(true);
    setQuestion('');
  };

  const getConfidenceClass = (confidence: QAResult['confidence']) => {
    switch (confidence) {
      case 'high':
        return 'confidence-high';
      case 'medium':
        return 'confidence-medium';
      default:
        return 'confidence-low';
    }
  };

  const getConfidenceIcon = (confidence: QAResult['confidence']) => {
    switch (confidence) {
      case 'high':
        return '✅';
      case 'medium':
        return '⚠️';
      default:
        return '❌';
    }
  };

  return (
    <div className="data-qa">
      <div className="qa-header">
        <div className="header-main">
          <h2>Ask About Your Data</h2>
          <p>Ask questions about {fileName} and receive evidence-backed answers.</p>
        </div>
        <div className="header-actions">
          <div className="ai-status">Data QA</div>
          {qaHistory.length > 0 && (
            <button onClick={clearHistory} className="clear-button">
              Clear History
            </button>
          )}
        </div>
      </div>

      <div className="qa-content">
        {showSuggestions && suggestions.length > 0 && (
          <div className="suggestions-section">
            <h3>Try asking:</h3>
            <div className="suggestions-grid">
              {suggestions.map((category) => (
                <div key={category.category} className="suggestion-category">
                  <div className="category-header">
                    <span className="category-icon">{category.icon}</span>
                    <span className="category-title">{category.category}</span>
                  </div>
                  <div className="category-questions">
                    {category.questions.map((item, index) => (
                      <button key={index} onClick={() => handleSuggestionClick(item)} className="suggestion-button">
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {qaHistory.length > 0 && (
          <div className="qa-history">
            {qaHistory.map((qa, index) => (
              <div key={`${qa.question}-${index}`} className="qa-item">
                <div className="question">
                  <span className="question-icon">❓</span>
                  <span className="question-text">{qa.question}</span>
                </div>
                <div className={`answer ${getConfidenceClass(qa.confidence)}`}>
                  <div className="answer-header">
                    <span className="answer-icon">🤖</span>
                    <div className="answer-meta">
                      <span className="confidence-indicator">
                        {getConfidenceIcon(qa.confidence)}
                        <span className="confidence-text">
                          {qa.confidence === 'high' ? 'High confidence' : qa.confidence === 'medium' ? 'Medium confidence' : 'Low confidence'}
                        </span>
                      </span>
                      <span className="processing-method">Deterministic service</span>
                    </div>
                  </div>
                  <div className="answer-text">{qa.answer}</div>

                  {qa.citations && qa.citations.length > 0 && (
                    <div className="related-insights">
                      <strong>Citations:</strong>
                      <ul>
                        {qa.citations.map((citation, citationIndex) => (
                          <li key={`${citation.label}-${citationIndex}`}>
                            <strong>{citation.label}:</strong> {citation.detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="qa-input-form">
        <div className="input-group">
          <input
            type="text"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Ask a question about your data..."
            className="qa-input"
            disabled={isLoading}
          />
          <button type="submit" disabled={!question.trim() || isLoading} className="submit-button">
            {isLoading ? '...' : 'Ask'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DataQA;
